import { ApolloError, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AlertTriangle, LogIn, RefreshCw, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerPetPicker } from './CustomerPetPicker';
import {
  ADD_DOCTOR_SALE_LINE_MUTATION,
  APPLY_AUTHORIZED_SALE_LINE_PRICE_OVERRIDE_MUTATION,
  CLEAR_SALE_ORDER_CURRENT_WEIGHT_MUTATION,
  DOCTOR_QUEUE_BOARD_QUERY,
  DOCTOR_SALE_ORDER_QUERY,
  MY_DOCTOR_ORDERS_QUERY,
  PET_PROFILE_WEIGHT_QUERY,
  REMOVE_DOCTOR_SALE_LINE_MUTATION,
  RETURN_TO_CASHIER_FOR_CORRECTION_MUTATION,
  SEND_SALE_ORDER_TO_CASHIER_MUTATION,
  SET_SALE_ORDER_CURRENT_WEIGHT_MUTATION,
  TAKE_DOCTOR_ORDER_MUTATION,
  UPDATE_DOCTOR_DRAFT_META_MUTATION,
  UPDATE_DOCTOR_SALE_LINE_MUTATION,
} from './doctor-pos.gql';
import { OrderLinesPanel } from './OrderLinesPanel';
import { PriceOverrideDialog } from './PriceOverrideDialog';
import { SellableSearchPanel, type SellableHit } from './SellableSearchPanel';
import { TakeOrderDialog } from './TakeOrderDialog';
import { WeightBanner } from './WeightBanner';

import { hasPermission } from '@/modules/auth/useAuth';
import { ClinicalVisitPanel } from '@/modules/clinical-records/ClinicalVisitPanel';
import { assertMutationSucceeded } from '@/shared/api/graphql-errors';
import {
  SALE_ORDER_ITEM_KIND_FROM_GQL,
  SALE_ORDER_ITEM_KIND_TO_GQL,
  SALE_ORDER_STATUS_FROM_GQL,
} from '@/shared/graphql/enums';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';
import { useDoctorPosStore } from '@/stores/doctorPosStore';

interface SaleOrderItem {
  id: string;
  line_no: number;
  item_kind: 'product' | 'service' | 'kit' | 'exception';
  product_id: string | null;
  service_id: string | null;
  kit_id: string | null;
  from_kit_component?: boolean;
  item_code: string;
  item_name: string;
  description: string | null;
  quantity: string;
  unit_price_cents: string;
  tax_rate_numer: string;
  tax_rate_denom: string;
  tax_cents: string;
  discount_cents: string;
  subtotal_cents: string;
  total_cents: string;
  is_price_overridden?: boolean;
  original_unit_price_cents?: string | null;
  override_unit_price_cents?: string | null;
  price_override_reason?: string | null;
  price_overridden_at?: string | null;
  kit_breakdown_lines?: KitBreakdownLine[];
}

interface KitBreakdownLine {
  item_kind: string;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
}

interface SaleOrder {
  id: string;
  status: string;
  customer_id: string | null;
  pet_id: string | null;
  return_reason: string | null;
  subtotal_cents: string;
  tax_cents: string;
  total_cents: string;
  clinical_notes: string | null;
  cashier_initial_notes: string | null;
  doctor_medical_notes: string | null;
  clinical_visit_id: string | null;
  sent_to_cashier_at: string | null;
  returned_to_doctor_at: string | null;
  taken_by_doctor_at: string | null;
  current_weight_grams: number | null;
  weight_measured_at: string | null;
  weight_source: string | null;
  weight_recorded_by: string | null;
  items: SaleOrderItem[];
}

interface DoctorOrderResponse {
  doctorSaleOrder: SaleOrder;
}

interface MyDoctorOrdersResponse {
  myDoctorOrders: { items: SaleOrder[]; page: { total: number } };
}

interface MedicalQueueCard {
  sale_order_id: string;
  customer_name: string | null;
  pet_name: string | null;
  pet_age_label: string | null;
  current_weight_grams: number | null;
  cashier_initial_notes: string | null;
  primary_item_label: string | null;
  consulting_room_name: string | null;
  queued_at: string;
}

interface MedicalQueueResponse {
  doctorQueueBoard: MedicalQueueCard[];
}

interface TakeDoctorOrderResponse {
  takeDoctorOrder: SaleOrder;
}

interface TakeDoctorOrderVariables {
  input: {
    sale_order_id: string;
    password: string;
  };
}

const EDITABLE_STATUSES: readonly SaleOrder['status'][] = [
  'in_medical_attention',
  'taken_by_doctor',
  // backward compatibility
  'draft',
  'returned_to_doctor',
];
const IDEMPOTENCY_HEADER = 'Idempotency-Key';

export function DoctorPosPage(): JSX.Element {
  const { t } = useTranslation();
  const apollo = useApolloClient();
  const active_draft_id = useDoctorPosStore((s) => s.active_draft_id);
  const setActiveDraft = useDoctorPosStore((s) => s.setActiveDraft);

  const [error_text, setErrorText] = useState<string | null>(null);
  const [notes_dirty, setNotesDirty] = useState<string | null>(null);
  const [override_target_id, setOverrideTargetId] = useState<string | null>(null);
  const [override_error, setOverrideError] = useState<string | null>(null);
  const [take_order_target_id, setTakeOrderTargetId] = useState<string | null>(null);
  const [take_order_error, setTakeOrderError] = useState<string | null>(null);
  const search_input_ref = useRef<HTMLInputElement | null>(null);
  const user_permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const can_request_price_override = hasPermission(user_permissions, 'pos.price_override.request');

  const { data: order_data, refetch: refetchOrder } = useQuery<DoctorOrderResponse>(
    DOCTOR_SALE_ORDER_QUERY,
    {
      variables: { id: active_draft_id ?? '' },
      skip: !active_draft_id,
      fetchPolicy: 'cache-and-network',
      onError: (err) => {
        if (apolloErrorIs(err, 'sale_order_not_found') || apolloErrorIs(err, 'not_owner')) {
          setActiveDraft(null);
        }
      },
    },
  );
  const order = order_data?.doctorSaleOrder ? normalizeSaleOrder(order_data.doctorSaleOrder) : null;

  const { data: my_orders_data, refetch: refetchMine } = useQuery<MyDoctorOrdersResponse>(
    MY_DOCTOR_ORDERS_QUERY,
    {
      variables: { offset: 0, limit: 12 },
      fetchPolicy: 'cache-and-network',
    },
  );
  const my_orders = (my_orders_data?.myDoctorOrders.items ?? []).map(normalizeSaleOrder);
  const { data: queue_data, refetch: refetchQueue } = useQuery<MedicalQueueResponse>(
    DOCTOR_QUEUE_BOARD_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 5_000,
    },
  );
  const queue_cards = queue_data?.doctorQueueBoard ?? [];

  const { data: pet_profile_data } = useQuery<{ pet: { id: string; weight_grams: number | null } }>(
    PET_PROFILE_WEIGHT_QUERY,
    {
      variables: { id: order?.pet_id ?? '' },
      skip: !order?.pet_id,
      fetchPolicy: 'cache-and-network',
    },
  );
  const pet_profile_weight_grams = pet_profile_data?.pet?.weight_grams ?? null;

  const [updateMeta, { loading: meta_saving }] = useMutation(UPDATE_DOCTOR_DRAFT_META_MUTATION);
  const [addLine, { loading: line_adding }] = useMutation(ADD_DOCTOR_SALE_LINE_MUTATION);
  const [updateLine, { loading: line_updating }] = useMutation(UPDATE_DOCTOR_SALE_LINE_MUTATION);
  const [removeLine, { loading: line_removing }] = useMutation(REMOVE_DOCTOR_SALE_LINE_MUTATION);
  const [sendToCashier, { loading: sending }] = useMutation(SEND_SALE_ORDER_TO_CASHIER_MUTATION);
  const [takeDoctorOrder, { loading: taking_order }] = useMutation<
    TakeDoctorOrderResponse,
    TakeDoctorOrderVariables
  >(TAKE_DOCTOR_ORDER_MUTATION);
  const [returnToCashier, { loading: returning_to_cashier }] = useMutation(
    RETURN_TO_CASHIER_FOR_CORRECTION_MUTATION,
  );
  const [applyPriceOverride, { loading: price_override_applying }] = useMutation(
    APPLY_AUTHORIZED_SALE_LINE_PRICE_OVERRIDE_MUTATION,
  );
  const [setWeight, { loading: weight_saving }] = useMutation(
    SET_SALE_ORDER_CURRENT_WEIGHT_MUTATION,
  );
  const [clearWeight, { loading: weight_clearing }] = useMutation(
    CLEAR_SALE_ORDER_CURRENT_WEIGHT_MUTATION,
  );

  const operation_busy =
    line_adding ||
    line_updating ||
    line_removing ||
    sending ||
    taking_order ||
    returning_to_cashier ||
    price_override_applying ||
    weight_saving ||
    weight_clearing;
  const is_busy = meta_saving || operation_busy;
  const is_editable = !!order && EDITABLE_STATUSES.includes(order.status);

  useEffect(() => {
    if (order) setNotesDirty(null);
  }, [order?.id]);

  // Persist notes after a debounce window.
  useEffect(() => {
    if (notes_dirty === null || !order) return;
    const handle = window.setTimeout(() => {
      void updateMeta({
        variables: { id: order.id, input: { doctor_medical_notes: notes_dirty } },
      }).catch((err) => apolloErrorMessage(err, t, setErrorText));
    }, 500);
    return () => window.clearTimeout(handle);
  }, [notes_dirty, order?.id, updateMeta, t]);

  const onAddSellable = useCallback(
    async (hit: SellableHit, quantity: string): Promise<void> => {
      const active_id = order?.id;
      if (!active_id) return;
      try {
        await addLine({
          variables: {
            input: {
              sale_order_id: active_id,
              item_kind:
                SALE_ORDER_ITEM_KIND_TO_GQL[hit.kind as keyof typeof SALE_ORDER_ITEM_KIND_TO_GQL],
              product_id: hit.kind === 'product' ? hit.id : null,
              service_id: hit.kind === 'service' ? hit.id : null,
              kit_id: hit.kind === 'kit' ? hit.id : null,
              quantity,
            },
          },
        });
      } catch (err) {
        apolloErrorMessage(err, t, setErrorText);
      }
    },
    [order?.id, addLine, t],
  );

  const onUpdateLineQty = useCallback(
    async (item_id: string, next_qty: string): Promise<void> => {
      try {
        await updateLine({
          variables: { input: { sale_order_item_id: item_id, quantity: next_qty } },
        });
      } catch (err) {
        apolloErrorMessage(err, t, setErrorText);
      }
    },
    [updateLine, t],
  );

  const onRemoveLine = useCallback(
    async (item_id: string): Promise<void> => {
      try {
        await removeLine({ variables: { sale_order_item_id: item_id } });
      } catch (err) {
        apolloErrorMessage(err, t, setErrorText);
      }
    },
    [removeLine, t],
  );

  const onRequestPriceOverride = useCallback((item_id: string): void => {
    setOverrideError(null);
    setOverrideTargetId(item_id);
  }, []);

  const onClosePriceOverride = useCallback((): void => {
    setOverrideTargetId(null);
    setOverrideError(null);
  }, []);

  const onSubmitPriceOverride = useCallback(
    async (args: {
      override_unit_price_cents: string;
      reason: string;
      otp_plaintext: string;
    }): Promise<void> => {
      if (!override_target_id) return;
      setOverrideError(null);
      try {
        await applyPriceOverride({
          variables: {
            input: {
              sale_order_item_id: override_target_id,
              override_unit_price_cents: args.override_unit_price_cents,
              reason: args.reason,
              otp_plaintext: args.otp_plaintext,
            },
          },
          context: { headers: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } },
        });
        setOverrideTargetId(null);
      } catch (err) {
        const message = priceOverrideErrorMessage(err, t);
        setOverrideError(message);
      }
    },
    [applyPriceOverride, override_target_id, t],
  );

  const onUpdateNotes = useCallback((notes: string): void => {
    setNotesDirty(notes);
  }, []);

  const onCaptureWeight = useCallback(
    async (args: {
      weight_grams: number;
      source: 'pos' | 'manual';
      reason?: string;
    }): Promise<void> => {
      if (!order) return;
      try {
        await setWeight({
          variables: {
            input: {
              sale_order_id: order.id,
              weight_grams: args.weight_grams,
              source: args.source,
              reason: args.reason ?? null,
            },
          },
          context: { headers: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } },
        });
      } catch (err) {
        apolloErrorMessage(err, t, setErrorText);
      }
    },
    [order?.id, setWeight, t],
  );

  const onClearWeight = useCallback(
    async (reason: string): Promise<void> => {
      if (!order) return;
      try {
        await clearWeight({
          variables: {
            input: { sale_order_id: order.id, reason },
          },
          context: { headers: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } },
        });
      } catch (err) {
        apolloErrorMessage(err, t, setErrorText);
      }
    },
    [order?.id, clearWeight, t],
  );

  const onTakeQueueOrder = useCallback((sale_order_id: string): void => {
    setErrorText(null);
    setTakeOrderError(null);
    setTakeOrderTargetId(sale_order_id);
  }, []);

  const onCloseTakeOrderDialog = useCallback((): void => {
    setTakeOrderTargetId(null);
    setTakeOrderError(null);
  }, []);

  const onSubmitTakeQueueOrder = useCallback(
    async (args: { password: string }): Promise<void> => {
      if (!take_order_target_id) return;
      try {
        const result = await takeDoctorOrder({
          variables: {
            input: {
              sale_order_id: take_order_target_id,
              password: args.password,
            },
          },
          context: { headers: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } },
        });
        assertMutationSucceeded(result, 'take_doctor_order_failed');
        const taken_id = result.data?.takeDoctorOrder?.id ?? take_order_target_id;
        setActiveDraft(taken_id);
        setTakeOrderTargetId(null);
        setTakeOrderError(null);
        await Promise.all([refetchMine(), refetchQueue()]);
      } catch (err) {
        setTakeOrderError(apolloErrorText(err, t));
      }
    },
    [take_order_target_id, takeDoctorOrder, setActiveDraft, refetchMine, refetchQueue, t],
  );

  const onReturnToCashier = useCallback(async (): Promise<void> => {
    if (!order) return;
    setErrorText(null);
    const note = window.prompt(t('doctor_pos.return_to_cashier_prompt')) ?? '';
    try {
      await returnToCashier({
        variables: { input: { sale_order_id: order.id, note } },
        context: { headers: { [IDEMPOTENCY_HEADER]: crypto.randomUUID() } },
      });
      setActiveDraft(null);
      await Promise.all([refetchMine(), refetchQueue()]);
    } catch (err) {
      apolloErrorMessage(err, t, setErrorText);
    }
  }, [order?.id, returnToCashier, setActiveDraft, refetchMine, refetchQueue, t]);

  const onSendToCashier = useCallback(async (): Promise<void> => {
    if (!order) return;
    setErrorText(null);
    try {
      await sendToCashier({ variables: { input: { sale_order_id: order.id } } });
      showToast({
        tone: 'success',
        title: t('doctor_pos.send_success_title'),
        body: t('doctor_pos.send_success_body'),
      });
      await Promise.all([refetchMine(), refetchQueue()]);
      setActiveDraft(null);
    } catch (err) {
      const message = apolloErrorText(err, t);
      setErrorText(message);
      showToast({
        tone: 'error',
        title: t('doctor_pos.send_error_title'),
        body: message,
      });
    }
  }, [order?.id, sendToCashier, refetchMine, refetchQueue, setActiveDraft, t]);

  // Keyboard shortcuts (rule: F2 search, F8 send, Esc cancel, Ctrl+S save).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'F2') {
        event.preventDefault();
        search_input_ref.current?.focus();
        return;
      }
      if (event.key === 'F8') {
        event.preventDefault();
        if (is_editable && order && order.items.length > 0) {
          void onSendToCashier();
        }
        return;
      }
      if (event.key === 'Escape') {
        if (search_input_ref.current && document.activeElement === search_input_ref.current) {
          search_input_ref.current.blur();
        }
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        // The draft auto-saves on every change so Ctrl+S simply forces a refetch
        // to surface the latest server state.
        if (active_draft_id) void refetchOrder();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [is_editable, order, onSendToCashier, refetchOrder, active_draft_id]);

  const send_disabled_reason = useMemo<string | null>(() => {
    if (!order) return t('doctor_pos.no_active_draft');
    if (!is_editable) return t('doctor_pos.locked_status', { status: order.status });
    if (!order.customer_id) return t('doctor_pos.customer_required');
    if (!order.pet_id) return t('doctor_pos.pet_required');
    if (order.items.length === 0) return t('doctor_pos.lines_required');
    return null;
  }, [order, is_editable, t]);
  const ignoreCustomerPetChange = useCallback((): void => undefined, []);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            {t('nav.doctor_pos')}
          </h2>
          <p className="text-muted-foreground text-sm">{t('doctor_pos.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {order ? (
            <Badge variant={order.status === 'returned_to_doctor' ? 'destructive' : 'muted'}>
              {t(`doctor_pos.status_${order.status}`)}
            </Badge>
          ) : null}
          {active_draft_id ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void apollo.cache.evict({ fieldName: 'doctorSaleOrder' });
                setActiveDraft(null);
              }}
              disabled={is_busy}
            >
              {t('doctor_pos.close_draft')}
            </Button>
          ) : null}
          {order ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void onReturnToCashier();
              }}
              disabled={!is_editable || is_busy}
            >
              <Undo2 className="h-4 w-4" aria-hidden />
              <span>{t('doctor_pos.return_to_cashier')}</span>
            </Button>
          ) : null}
        </div>
      </header>

      {order?.status === 'returned_to_doctor' ? (
        <div className="border-destructive/40 bg-destructive/5 flex items-start gap-3 rounded-md border p-3 text-sm">
          <AlertTriangle className="text-destructive mt-0.5 h-4 w-4" aria-hidden />
          <div>
            <p className="text-destructive font-semibold">
              {t('doctor_pos.returned_banner_title')}
            </p>
            <p className="text-destructive/80">
              {order.return_reason ?? t('doctor_pos.returned_no_reason')}
            </p>
          </div>
        </div>
      ) : null}

      {error_text ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm">
          {error_text}
        </div>
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('doctor_pos.identification_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {order ? (
                <CustomerPetPicker
                  customer_id={order.customer_id}
                  pet_id={order.pet_id}
                  onChange={ignoreCustomerPetChange}
                  disabled
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t('doctor_pos.pick_waiting_patient')}
                </p>
              )}
            </CardContent>
          </Card>

          {order?.clinical_visit_id ? <ClinicalVisitPanel sale_order_id={order.id} /> : null}
        </div>

        <div className="space-y-4">
          {order ? (
            <>
              <WeightBanner
                current_weight_grams={order.current_weight_grams}
                weight_source={order.weight_source}
                profile_weight_grams={pet_profile_weight_grams}
                disabled={!is_editable}
                busy={is_busy}
                onCapture={onCaptureWeight}
                onClear={onClearWeight}
              />
              {order.cashier_initial_notes ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t('doctor_pos.cashier_initial_note')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{order.cashier_initial_notes}</p>
                  </CardContent>
                </Card>
              ) : null}
              <OrderLinesPanel
                items={order.items}
                total_cents={order.total_cents}
                subtotal_cents={order.subtotal_cents}
                tax_cents={order.tax_cents}
                clinical_notes={
                  notes_dirty ?? order.doctor_medical_notes ?? order.clinical_notes ?? ''
                }
                is_editable={is_editable}
                is_busy={operation_busy}
                is_saving_notes={meta_saving}
                send_disabled_reason={send_disabled_reason}
                can_request_price_override={can_request_price_override}
                onUpdateQty={onUpdateLineQty}
                onRemove={onRemoveLine}
                onUpdateNotes={onUpdateNotes}
                onSend={onSendToCashier}
                onRequestPriceOverride={onRequestPriceOverride}
              />
            </>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('doctor_pos.no_active_draft')}</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-4 text-center text-sm">
                <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
                <p>{t('doctor_pos.pick_waiting_patient')}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">{t('doctor_pos.search_title')}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  void refetchOrder();
                }}
                disabled={!order || is_busy}
                aria-label={t('doctor_pos.refresh')}
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
              </Button>
            </CardHeader>
            <CardContent>
              <SellableSearchPanel
                ref={search_input_ref}
                onAdd={onAddSellable}
                disabled={!order || !is_editable}
                preview_context={
                  order
                    ? {
                        current_weight_grams: order.current_weight_grams ?? null,
                        customer_id: order.customer_id ?? null,
                      }
                    : null
                }
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('doctor_pos.waiting_queue_title')} · {queue_cards.length}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queue_cards.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t('doctor_pos.waiting_queue_empty')}
                </p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {queue_cards.map((card) => (
                    <li key={card.sale_order_id} className="border-border rounded-md border p-2">
                      <p className="font-medium">{card.pet_name ?? t('doctor_pos.unknown_pet')}</p>
                      <p className="text-muted-foreground text-xs">
                        {card.customer_name ?? t('doctor_pos.unknown_customer')}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {card.consulting_room_name ?? t('doctor_pos.no_consulting_room')}
                      </p>
                      {card.primary_item_label ? (
                        <p className="text-muted-foreground mt-1 text-xs">
                          {card.primary_item_label}
                        </p>
                      ) : null}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-muted-foreground text-xs">
                          {timeLabel(card.queued_at)}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            void onTakeQueueOrder(card.sale_order_id);
                          }}
                          disabled={is_busy}
                        >
                          <LogIn className="h-4 w-4" aria-hidden />
                          <span>{t('doctor_pos.take_order')}</span>
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('doctor_pos.my_orders_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {my_orders.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('doctor_pos.my_orders_empty')}</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {my_orders.map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        className={`hover:bg-secondary flex w-full items-center justify-between rounded-md px-2 py-1 ${
                          o.id === active_draft_id ? 'bg-secondary' : ''
                        }`}
                        onClick={() => setActiveDraft(o.id)}
                      >
                        <span className="truncate">
                          {o.id.slice(0, 8)} · {t(`doctor_pos.status_${o.status}`)}
                        </span>
                        <span className="text-muted-foreground font-mono text-xs tabular-nums">
                          {o.items.length}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <p className="text-muted-foreground text-xs">{t('doctor_pos.shortcuts_hint')}</p>
        </div>
      </div>
      {override_target_id && order
        ? (() => {
            const target = order.items.find((it) => it.id === override_target_id);
            if (!target) return null;
            return (
              <PriceOverrideDialog
                line_item_name={target.item_name}
                line_quantity={target.quantity}
                current_unit_price_cents={target.unit_price_cents}
                submitting={price_override_applying}
                error_text={override_error}
                on_close={onClosePriceOverride}
                on_submit={onSubmitPriceOverride}
              />
            );
          })()
        : null}
      {take_order_target_id ? (
        <TakeOrderDialog
          submitting={taking_order}
          error_text={take_order_error}
          on_close={onCloseTakeOrderDialog}
          on_submit={onSubmitTakeQueueOrder}
        />
      ) : null}
    </div>
  );
}

function priceOverrideErrorMessage(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApolloError) {
    const code = err.graphQLErrors[0]?.message ?? err.message;
    if (code === 'otp_invalid') return t('otp.error_invalid');
    if (code === 'otp_expired') return t('otp.error_expired');
    if (code === 'override_unit_price_must_be_non_negative')
      return t('doctor_pos.price_override_invalid_amount');
    if (code === 'price_override_reason_required')
      return t('doctor_pos.price_override_reason_required');
    if (code.startsWith('order_not_editable')) return t('doctor_pos.order_locked');
    return code;
  }
  if (err instanceof Error) return err.message;
  return 'unknown_error';
}

function apolloErrorIs(error: ApolloError | undefined, code: string): boolean {
  if (!error) return false;
  return error.graphQLErrors.some((g) => g.message.includes(code));
}

function timeLabel(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function normalizeSaleOrder(order: SaleOrder): SaleOrder {
  return {
    ...order,
    status:
      SALE_ORDER_STATUS_FROM_GQL[order.status as keyof typeof SALE_ORDER_STATUS_FROM_GQL] ??
      order.status,
    items: order.items.map((item) => ({
      ...item,
      item_kind:
        SALE_ORDER_ITEM_KIND_FROM_GQL[
          item.item_kind as keyof typeof SALE_ORDER_ITEM_KIND_FROM_GQL
        ] ?? 'exception',
    })),
  };
}

function apolloErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
  setError: (msg: string | null) => void,
): void {
  setError(apolloErrorText(err, t));
}

function apolloErrorText(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof ApolloError) {
    const code = err.graphQLErrors[0]?.message ?? err.message;
    if (code.startsWith('insufficient_stock')) {
      return t('errors.insufficient_stock');
    }
    if (code.startsWith('invalid_fractional_quantity')) {
      return t('doctor_pos.error_invalid_fractional');
    }
    if (code.startsWith('current_weight_required')) {
      return t('doctor_pos.error_current_weight_required');
    }
    if (code === 'manual_weight_reason_required') {
      return t('doctor_pos.weight_reason_required');
    }
    if (code === 'current_weight_must_be_positive_int') {
      return t('doctor_pos.weight_invalid');
    }
    if (code === 'customer_required') {
      return t('doctor_pos.customer_required');
    }
    if (code === 'pet_required') {
      return t('doctor_pos.pet_required');
    }
    if (code === 'order_empty') {
      return t('doctor_pos.lines_required');
    }
    if (code === 'workstation_required') {
      return t('errors.workstation_required');
    }
    if (code.startsWith('order_not_editable')) {
      return t('doctor_pos.order_locked');
    }
    if (code === 'invalid_credentials') {
      return t('auth.invalid_credentials');
    }
    if (code === 'doctor_permission_missing') {
      return t('doctor_pos.error_doctor_permission_missing');
    }
    if (code === 'doctor_session_mismatch') {
      return t('doctor_pos.error_doctor_session_mismatch');
    }
    if (code === 'consulting_room_already_occupied') {
      return t('doctor_pos.error_consulting_room_occupied');
    }
    if (code === 'consulting_room_not_configured_for_workstation') {
      return t('doctor_pos.error_consulting_room_not_configured');
    }
    return code;
  }
  if (err instanceof Error) return err.message;
  return 'unknown_error';
}
